import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Download, Paperclip, X } from 'lucide-react';
import { Button } from '../ui/button';
import { extractErrorMessage } from '../../utils/apiErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/unit-files`;
const MAX_BYTES = 15 * 1024 * 1024;

export const UnitAttachments = ({ ids = [], onChange, onBusyChange, disabled = false, prefix = 'unit-files', maxFiles = 20 }) => {
  const [files, setFiles] = useState({}), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [retry, setRetry] = useState(0), [lookupFailed, setLookupFailed] = useState(false);
  const input = useRef(null);
  const key = JSON.stringify(ids);
  useEffect(() => {
    let active = true;
    setLookupFailed(false);
    const attachment_ids = JSON.parse(key);
    if (attachment_ids.length) Promise.all(Array.from({ length: Math.ceil(attachment_ids.length / 200) }, (_, index) => axios.post(`${API}/lookup`, { attachment_ids: attachment_ids.slice(index * 200, (index + 1) * 200) })))
      .then((responses) => { if (active) {
        const data = { files: responses.flatMap((response) => response.data.files) };
        setFiles((prev) => ({ ...prev, ...Object.fromEntries(data.files.map((f) => [f.id, f])) }));
        if (data.files.length < new Set(attachment_ids).size) { setLookupFailed(true); setError('Some attachments are unavailable or no longer accessible.'); }
      } })
      .catch((e) => { if (active) { setLookupFailed(true); setError(extractErrorMessage(e, 'Unable to load file details')); } });
    return () => { active = false; };
  }, [key, retry]);

  const upload = async (selected) => {
    if (busy || disabled || !selected.length) return;
    setError('');
    if (ids.length + selected.length > maxFiles) return setError(`Maximum ${maxFiles} files per asset.`);
    if (selected.some((f) => !/\.(pdf|jpe?g)$/i.test(f.name) || !['application/pdf', 'image/jpeg'].includes(f.type))) return setError('Only PDF and JPEG files are allowed.');
    if (selected.some((f) => f.size > MAX_BYTES || !f.size)) return setError('Choose non-empty files up to 15 MB each.');
    setBusy(true); onBusyChange?.(true);
    const next = [...ids];
    try {
      for (const file of selected) {
        const body = new FormData(); body.append('file', file);
        const { data } = await axios.post(API, body);
        setFiles((prev) => ({ ...prev, [data.id]: data }));
        next.push(data.id); onChange([...next]);
      }
    } catch (e) { setError(extractErrorMessage(e, 'Upload failed. Successfully uploaded files have been kept; retry the remaining files.')); }
    finally { setBusy(false); onBusyChange?.(false); }
  };

  const download = async (id) => {
    setBusy(true); setError('');
    try {
      const { data } = await axios.get(`${API}/${id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(data), link = document.createElement('a');
      link.href = url; link.download = files[id]?.filename || 'attachment'; document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { setError('File unavailable or no longer accessible. Please try again.'); }
    finally { setBusy(false); }
  };

  return <div className="space-y-2 min-w-0" data-testid={prefix}>
    {onChange && <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(Array.from(e.dataTransfer.files)); }} className="border border-dashed border-slate-300 rounded-md p-3" data-testid={`${prefix}-dropzone`}>
      <input ref={input} type="file" multiple accept=".pdf,.jpg,.jpeg,application/pdf,image/jpeg" className="hidden" disabled={disabled || busy} onChange={(e) => { upload(Array.from(e.target.files)); e.target.value = ''; }} data-testid={`${prefix}-input`} />
      <Button type="button" size="sm" variant="outline" disabled={disabled || busy || ids.length >= maxFiles} onClick={() => input.current?.click()} data-testid={`${prefix}-upload`}><Paperclip size={14} className="mr-2" />{busy ? 'Please wait…' : 'Attach PDF / JPEG'}</Button>
      <p className="text-xs text-slate-500 mt-2" data-testid={`${prefix}-limits`}>PDF, JPG or JPEG · 15 MB each · {ids.length}/{maxFiles} files</p>
    </div>}
    {ids.map((id) => <div className="flex gap-2 items-start min-w-0" key={id} data-testid={`${prefix}-row-${id}`}>
      <Button type="button" variant="ghost" className="h-auto min-h-9 min-w-0 flex-1 justify-start px-2 py-2 text-left" disabled={busy || !files[id]} onClick={() => download(id)} data-testid={`${prefix}-download-${id}`}><Download size={14} className="shrink-0 mr-2" /><span className="min-w-0 break-all whitespace-normal" data-testid={`${prefix}-name-${id}`}>{files[id]?.filename || (lookupFailed ? 'Attachment unavailable' : 'Loading attachment…')}</span></Button>
      {onChange && <Button type="button" variant="ghost" size="icon" className="shrink-0" disabled={disabled || busy} onClick={() => onChange(ids.filter((v) => v !== id))} aria-label={`Remove ${files[id]?.filename || 'attachment'}`} title="Remove attachment" data-testid={`${prefix}-remove-${id}`}><X size={14} /></Button>}
    </div>)}
    {error && <p className="text-sm text-red-600 break-words" role="alert" data-testid={`${prefix}-error`}>{error}</p>}
    {lookupFailed && <Button type="button" size="sm" variant="outline" onClick={() => { setError(''); setRetry((v) => v + 1); }} data-testid={`${prefix}-retry`}>Retry file details</Button>}
  </div>;
};